import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { compareGrades, createReportSearchFuse, filterRowsBySearchText } from './reportUtils';

export type ReportColumnSortType = 'text' | 'grade' | 'number' | 'boolean';

export type ReportColumn = {
	id: string;
	label: string;
	sortable?: boolean;
	sortType?: ReportColumnSortType;
};

export type ReportTableRow = {
	key: string;
	cells: Array<React.ReactNode>;
	sortValues: Array<string | number | boolean | null | undefined>;
	searchText: string;
};

type SortState = {
	columnId: string;
	direction: 'asc' | 'desc';
} | null;

type ReportTableProps = {
	columns: ReportColumn[];
	rows: ReportTableRow[];
	emptyMessage?: string;
	searchPlaceholder?: string;
};

function compareSortValues(
	a: string | number | boolean | null | undefined,
	b: string | number | boolean | null | undefined,
	sortType: ReportColumnSortType
): number {
	if (sortType === 'grade') {
		return compareGrades(String(a ?? ''), String(b ?? ''));
	}
	if (sortType === 'number') {
		return Number(a ?? 0) - Number(b ?? 0);
	}
	if (sortType === 'boolean') {
		return Number(Boolean(a)) - Number(Boolean(b));
	}
	return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });
}

const ReportTable = ({
	columns,
	rows,
	emptyMessage = 'No rows match the current filters.',
	searchPlaceholder = 'Search this report…',
}: ReportTableProps) => {
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<SortState>(null);

	const query = search.trim();
	const fuse = useMemo(() => createReportSearchFuse(rows), [rows]);

	const visibleRows = useMemo(() => {
		const next = filterRowsBySearchText(rows, query, fuse);
		if (!sort) return next;

		const columnIndex = columns.findIndex((column) => column.id === sort.columnId);
		if (columnIndex < 0) return next;
		const column = columns[columnIndex];
		const sortType = column.sortType ?? 'text';

		return [...next].sort((a, b) => {
			const result = compareSortValues(
				a.sortValues[columnIndex],
				b.sortValues[columnIndex],
				sortType
			);
			return sort.direction === 'asc' ? result : -result;
		});
	}, [columns, fuse, query, rows, sort]);

	const handleSort = (column: ReportColumn) => {
		if (column.sortable === false) return;
		setSort((current) => {
			if (current?.columnId !== column.id) {
				return { columnId: column.id, direction: 'asc' };
			}
			if (current.direction === 'asc') {
				return { columnId: column.id, direction: 'desc' };
			}
			return null;
		});
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative max-w-sm min-w-[220px] flex-1">
					<Search
						size={14}
						className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-(--header-text)"
					/>
					<Input
						type="search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={searchPlaceholder}
						aria-label="Search report"
						className="h-9 pl-8"
					/>
				</div>
				{query ? (
					<p className="text-sm text-(--header-text)" aria-live="polite">
						Showing {visibleRows.length} of {rows.length}
					</p>
				) : null}
			</div>

			{visibleRows.length === 0 ? (
				<div className="rounded-[6px] border border-[rgba(0,15,25,0.08)] px-4 py-8 text-center text-sm text-(--header-text)">
					{emptyMessage}
				</div>
			) : (
				<div className="max-h-[min(70vh,720px)] overflow-auto rounded-[6px] border border-[rgba(0,15,25,0.08)]">
					<table className="w-full min-w-[720px] border-collapse text-left text-sm">
						<thead className="sticky top-0 z-10 bg-[rgba(245,247,249,1)] shadow-[0_1px_0_rgba(0,15,25,0.08)]">
							<tr>
								{columns.map((column) => {
									const sortable = column.sortable !== false;
									const isActive = sort?.columnId === column.id;
									return (
										<th
											key={column.id}
											className="border-b border-[rgba(0,15,25,0.08)] px-3 py-2 font-semibold text-(--header-text) whitespace-nowrap"
										>
											{sortable ? (
												<button
													type="button"
													onClick={() => handleSort(column)}
													className={cn(
														'inline-flex items-center gap-1 rounded-sm bg-transparent p-0 font-semibold text-(--header-text) hover:text-(--body-text)',
														isActive && 'text-(--body-text)'
													)}
													aria-label={`Sort by ${column.label}`}
												>
													{column.label}
													{isActive && sort.direction === 'asc' ? (
														<ArrowUp size={13} />
													) : isActive && sort.direction === 'desc' ? (
														<ArrowDown size={13} />
													) : (
														<ArrowUpDown size={13} className="opacity-40" />
													)}
												</button>
											) : (
												column.label
											)}
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{visibleRows.map((row) => (
								<tr key={row.key} className="odd:bg-white even:bg-[rgba(0,15,25,0.015)]">
									{columns.map((column, cellIndex) => (
										<td
											key={column.id}
											className="border-b border-[rgba(0,15,25,0.06)] px-3 py-2 align-top text-(--body-text)"
										>
											{row.cells[cellIndex]}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default ReportTable;
